import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import MainLayout from "../../components/layout/MainLayout";
import Breadcrumbs from "../../components/common/Breadcrumbs";
import IntegratedInitialAssessmentForm from "./IntegratedAssessmentForm";
import { createIntegratedAssessment } from "../../store/slices/integratedAssessmentSlice";
import { fetchPatient } from "../../store/slices/patientsSlice";
import { getApiErrorMessage } from "../../utils/apiError";

const AddIntegratedInitialAssessment = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { state } = useLocation();
    const { caseId, patientId } = state || {};
    const [savingSection, setSavingSection] = useState(null);

    // Helper to convert API array format to form format
    const normalizeArrayData = (apiData, fieldName) => {
        if (!apiData || !Array.isArray(apiData)) return [];
        return apiData.map(item => ({
            id: item.id || Math.random(),
            ...item
        }));
    };

    const getDefaultFormData = () => ({
        patientId: patientId || "",
        caseId: caseId || "",
        // New one-to-one sections (nested objects, snake_case keys)
        cognitive_assessment: {},
        discharge_planning: {},
        mrNumber: "",
        date: new Date().toISOString().split('T')[0],
        time: "",
        name: "",
        age: "",
        occupation: "",
        gender: "",
        maritalStatus: "",
        sourceOfReferral: "",
        informant: "",
        comorbid: "",
        consentType: "",
        consentReason: "",
        presentingComplaints: "",
        psychoSocialStressors: "",
        functionalAdjustment: "",
        vegetativeFunctions: "",
        currentTreatment: "",
        allergies: "",
        duration: "",
        episodes: "",
        generalProblems: [{ id: 1, problem: "" }],
        ect: "",
        course: "",
        compliance: "",
        admission: "",
        opdFU: "",
        lai: "",
        epsSideEffects: "",
        roh: "",
        ros: "",
        rov: "",
        roe: "",
        rof: "",
        rovu: "",
        suicideFormNursing: "",
        violenceFormNursing: "",
        suicideFormNursing2: "",
        violenceFormNursing2: "",

        // Part One
        medicalSurgicalHistory: "",
        personalHistory: "",
        educationalHistory: "",
        occupationalHistory: "",
        spiritualHistory: "",
        maritalHistory: "",
        legalIssues: "",
        preMorbidPersonality: "",
        substanceUse: "",
        familyHistory: "",
        familyOrigin: [],
        familyPsychIllness: false,
        generalAppearance: "",
        cranialNerve: "",
        sensorySystem: "",
        motorSystem: "",
        meningealIrritation: "",
        cerebellarSigns: "",
        pallor: "",
        jaundice: "",
        cyanosis: "",
        cutMarks: "",
        clubing: "",
        thyroid: "",
        lymphNodes: "",
        burnMarks: "",
        dehydration: "",
        edema: "",
        abnormalMovementOrTremors: "",
        bulkRightUpper: "",
        bulkRightLower: "",
        bulkLeftUpper: "",
        bulkLeftLower: "",
        toneRightUpper: "",
        toneRightLower: "",
        toneLeftUpper: "",
        toneLeftLower: "",
        powerRightUpper: "",
        powerRightLower: "",
        powerLeftUpper: "",
        powerLeftLower: "",
        reflexesRightUpper: "",
        reflexesRightLower: "",
        reflexesLeftUpper: "",
        reflexesLeftLower: "",

        // Part Two
        respiratoryInspection: "", respiratoryPalpation: "", respiratoryPercussion: "", respiratoryAuscultation: "",
        abdominalInspection: "", abdominalPalpation: "", abdominalPercussion: "", abdominalAuscultation: "",
        cardioInspection: "", cardioPalpation: "", cardioPercussion: "", cardioAuscultation: "",
        akathisia: "", dystonia: "", td: "", parkinsons: "",
        provisionalDiagnosis: "",
        differentialDiagnosis: "",
        diagnosisTreatmentDetails: "",
        consentTakenClient: false,
        consentTakenFamily: false,
        consentTakenGuardian: false,
        consentTakenTerm: "",
        typeOfAdmission: "Elective",
        unit: "",
        dateOfAdmission: "",
        timeOfAdmission: "",
        modeOfTransportation: "",
        approxLengthOfStay: "",
        diagnosticWorkup: "",
        pharmacotherapy: "",
        treatmentOthers: "",
        consultants: "",
        residentName: "",
        cellNumber: "",
        residentSignDate: "",
        residentTime: "",
        doctorsName: "",
        consultantSignDate: "",
        consultantTime: "",
        accompaniedBy: "",
        idBand: "",
        nursingAge: "",
        nursingWeight: "0",
        nursingHeight: "",
        bloodPressure: "",
        temperature: "",
        pulse: "",
        respRate: "",
        o2Saturation: "",
        reasonForAdmission: "",
        communicableDisease: [],
        communicable_None: "",
        communicable_Others: "",
        isolationType: "",
        isolationOther: "",
        eyeGlasses: "",
        hearingAids: "",
        dentures: "",
        jewelry: "",
        personalOthers: "",

        // Part Three
        mdrOrganisms: [],
        informedValuablePolicy: "",
        presenceOfPain: "",
        painScore: "",
        painScaleUsed: [],
        painLocation: "",

        // Part Four
        levelOfConscious: "",
        sensory: "",
        sensoryOthers: "",
        vision: "",
        visionOthers: "",
        hearing: "",
        hearingOthers: "",
        speech: "",
        speechOthers: "",
        selfCaring: [],
        selfCaringOther: "",
        musculoskeletal: [],
        musculoskeletalOther: "",
        assisting: [],
        assistingOther: "",
        admissionItems: [],
        cognition: [],
        clientLevel: "",
        clientLevelOthers: "",
        roomAllocation: "",
        informMedicine: "",
        guideFamilyMeetings: "",
        infoConsultants: "",
        authorizeHairCutting: "",
        isolationContagious: "",
        weightKg: "0",
        heightCm: "0",
        bmi: "0",
        bmiLess18: "",
        bmiGreater25: "",
        lostWeight3Months: "",
        recentHospitalization: "",
        dietitianReferralConditions: "",
        referDietitian: "",
        rohNursing: "",
        rosNursing: "",
        rovNursing: "",
        roeNursing: "",
        rofNursing: "",
        rovuNursing: "",
        safetyCarePlanNursing: "",
        safetyCarePlanNursing2: "",
        appearance: "",
        behavior: "",
        speechMse: "",
        moodAffect: "",
        thoughtProcess: "",
        thoughtContent: "",
        insight: "",
        memory: "",
        selfEsteem: "",
        hopelessness: "",
        worthlessness: "",
        coping: "",
        maritalStatusSocio: "",
        noOfChildren: "",
        workSituation: "",
        occupationSocio: "",
        habit: "",
        habitSpecify: "",
        livingSituation: "",
        livingSituationSpecify: "",

        // Part Five
        religion: "",
        religionOther: "",
        religiousPractices: "",
        privacy: "",
        food: "",
        genderRegalia: "",
        otherValues: "",
        mouthTongue: "",
        oralSignSymptom: "",
        oralSignSymptomOthers: "",
        ngGastric: "",
        ngGastricDate: "",
        skinAppearance: "",
        skinAppearanceOther: "",
        skinTemperature: "",
        skinContagious: "",
        skinLocation: "",
        skinReasons: "",
        cardioPulse: "",
        cardioSignSymptom: "",
        cardioSignSymptomOther: "",
        respiration: "",
        respirationOther: "",
        cough: "",
        bowelMovement: "",
        bowelSignSymptom: "",
        urinaryAppearance: "",
        urinarySignSymptom: "",
        dateOfInsertion: "",
        catheterType: "",
        urinaryOthers: "",
        obsGyn: "",
        menstrualCycle: "",
        dysmenorrhea: "",
        skinChanges: "",
        obsGynOthers: "",
        nursingStaff: "",
        checkedBy: "",
        designation: "",
        nursingDate: "",
        nursingTime: "",
        signature: "",
        headNurse: "",
        clinicalNurseInstructor: "",
        teamLeader: "",
        irpName: "",
        irpDate: "",
        domain: "",
        rehabGoal: "",
        strengths: "",
        skillsNeeded: "",
        clientSignature: "",
        clientDate: "",
        practitionerSignature: "",
        practitionerDate: "",
        endorsement: "",

        // Part Six - all empty, user must fill in
        discharge: "",
        dischargeInstructionTo: "",
        specialInstructions: "",
        injections: "",
        anyRedFlag: "",
        riskAssessmentDC: "",
        expectedDischargeDate: "",
        homeCare: "",
        homeCareOthersSpecify: "",
        transportation: "",
        psychotherapy: "",
        dayRehab: "",
        dayRehabDays: "",
        longTermCare: "",
        otherNeeds: "",
        otherNeedsSpecify: "",
        educateIllness: "",
        educateHygiene: "",
        educateMedication: "",
        unusualBehavior: "",
        givingMedicine: "",
        contactDoctor: "",
        clientSharing: "",
        engagedActivities: "",
        triggerRelapse: "",
        sleepWake: "",
        eatingMeals: "",
        physicalActivity: "",
        copingStrategies: "",
        preventingRelapse: "",
        sociallyConnected: "",
        diet: "",
        takeHomeMedication: "",
        complianceTreatment: "",
        followupVisit: "",
        effectiveCoping: "",
        hospitalEmergency: "",
        activityHome: "",
        stressManagement: "",
        aggressionManagement: "",
        alarmingSigns: "",
        occupationalTherapy: "",
        socialSupport: "",
        environmentalSafety: "",
        processGiverRoleStrain: "",
        longTermFacilities: "",
        contactNumber: "",
        others: "",
        nameSignature: "",
        signatureDate: "",
        signatureTime: "",
        possibleRelapseSign: "",
        recoveryGoals: "",
        whoToContact: "",
        whereToGoCrises: "",
        detailsMedication: "",
        detailTreatmentPlan: "",
        physicalHealthNeeds: "",
        dateOfReview: new Date().toISOString().split('T')[0],
        admissionPathway: "",
        riskAssessmentSafetyPlan: "",
        doctorSignature: "",
        doctorName: "",
        guardianSignature: "Signed",
        guardianName: "",
        nameOfDocuments: "Integrated Assessment Form",
        effectiveDate: "",
        approvedBy: "",
        approvedDate: "",
        nextRevisionBy: "",
        nextRevisionDate: "",

        // Monthly Progress Notes
        progressNote: "",
        familyMeetingMinutes: "",
        discussionSummary: "",



        // Arrays - Using form field names (start empty)
        nursingProblems: [{ id: Date.now(), problem: "" }],
        objActPlan: [{ id: Date.now(), objective: "", actionStatus: "Pending", date: new Date().toISOString().split('T')[0], steps: "" }],
        supportingData: [{ id: Date.now(), data: "" }],
        objectives: [{ id: Date.now(), name: "", obj_status: "Pending", obj_date: new Date().toISOString().split('T')[0], actions: [] }],
        monthlyProgress: [{ id: Date.now(), familyMeetingMinutes: "", discussionSummary: "", meetingDate: "", update: "", progressDate: "" }],

        // API format (for submission)
        nursing_needs: [],
        obj_act_plan: [],
        problem_list: [],
    });

    const [formData, setFormData] = useState(getDefaultFormData());

    useEffect(() => {
        if (formData.patientId) {
            dispatch(fetchPatient(formData.patientId))
                .unwrap()
                .then((patient) => {
                    if (patient) {
                        const calculatedAge = (dobString) => {
                            if (!dobString) return "";
                            const today = new Date();
                            const birthDate = new Date(dobString);
                            let age = today.getFullYear() - birthDate.getFullYear();
                            const m = today.getMonth() - birthDate.getMonth();
                            if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
                                age--;
                            }
                            return age;
                        };
                        setFormData((prev) => ({
                            ...prev,
                            name: `${patient.first_name || ""} ${patient.last_name || ""}`.trim(),
                            gender: patient.gender || "",
                            age: calculatedAge(patient.dob),
                            nursingAge: patient.dob ? patient.dob.split('T')[0] : "",
                            mrNumber: patient.mr_no || "",
                        }));
                    }
                })
                .catch((err) => console.error("Failed to fetch patient:", err));
        }
    }, [dispatch, formData.patientId]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (type === "checkbox" && name === "familyOrigin") {
            setFormData((prev) => ({
                ...prev,
                [name]: checked
                    ? [...(prev[name] || []), value]   // <-- ensure array exists
                    : (prev[name] || []).filter((item) => item !== value),
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const validateRequiredFields = () => {
        if (!formData.mrNumber || !formData.date || !formData.time) {
            Swal.fire({
                title: "Validation Error!",
                text: "Please fill in the MR Number, Date, and Time before submitting.",
                icon: "warning",
                confirmButtonColor: "#3085d6",
            });
            return false;
        }
        return true;
    };

    // Handle section-wise save - Creates full entry and redirects to edit
    const handleSectionSave = async (section) => {
        if (!validateRequiredFields()) return;
        setSavingSection(section);
        try {
            const payload = buildFullPayload();
            const res = await dispatch(createIntegratedAssessment(payload)).unwrap();

            Swal.fire({
                title: "Created!",
                text: `Assessment created successfully.`,
                icon: "success",
                timer: 1500,
                didClose: () => {
                    navigate(`/residential/phase-01/integrated-initial-assessment/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
                }
            });
        } catch (err) {
            Swal.fire("Error!", `Failed to create assessment. ${getApiErrorMessage(err, '')}`, "error");
        } finally {
            setSavingSection(null);
        }
    };

    // Build section-specific payload
    const buildSectionPayload = (section) => {
        const basePayload = {
            patient_id: formData.patientId,
            case_id: formData.caseId,
            mr_number: formData.mrNumber,
            date: formData.date,
            time: formData.time,
            name: formData.name,
            age: parseInt(formData.age) || 0,
            occupation: formData.occupation,
            gender: formData.gender,
            marital_status: formData.maritalStatus,
            source_of_referral: formData.sourceOfReferral,
            informant: formData.informant,
            comorbid: formData.comorbid,
            consent_type: formData.consentType,
            consent_reason: formData.consentReason,
            presenting_complaints: formData.presentingComplaints,
            psycho_social_stressors: formData.psychoSocialStressors,
            functional_adjustment: formData.functionalAdjustment,
            vegetative_functions: formData.vegetativeFunctions,
            current_treatment: formData.currentTreatment,
            allergies: formData.allergies,
            duration: formData.duration,
            episodes: parseInt(formData.episodes) || 0,
            ect: formData.ect || "",
            course: formData.course,
            compliance: formData.compliance,
            admission: formData.admission,
            opd_fu: formData.opdFU,
            lai: formData.lai,
            eps_side_effects: formData.epsSideEffects,
            roh: formData.roh,
            ros: formData.ros,
            rov: formData.rov,
            roe: formData.roe,
            rof: formData.rof,
            rovu: formData.rovu,
            risk_of_suicide: formData.suicideFormNursing,
            risk_of_violence: formData.violenceFormNursing,
            time_of_discharge: formData.safetyCarePlanNursing,
        };

        // Add section-specific data
        if (section === "Part One") {
            return {
                ...basePayload,
                part_one: buildPartOne(),
                part_two: buildPartTwo(),
                part_three: buildPartThree(),
                part_four: buildPartFour(),
                part_five: buildPartFive(),
                part_six: buildPartSix(),
                monthly_pro_notes: buildMonthlyNotes(),
                nursing_needs: formData.nursing_needs,
                obj_act_plan: formData.obj_act_plan,
                problem_list: formData.problem_list,
                cognitive_assessment: formData.cognitive_assessment,
                discharge_planning: formData.discharge_planning,
            };
        }

        return basePayload;
    };

    const buildPartOne = () => ({
        medical_surgical_history: formData.medicalSurgicalHistory,
        personal_history: formData.personalHistory,
        educational_history: formData.educationalHistory,
        occupational_history: formData.occupationalHistory,
        spiritual_history: formData.spiritualHistory,
        marital_history: formData.maritalHistory,
        legal_issues: formData.legalIssues,
        pre_morbid_personality: formData.preMorbidPersonality,
        substance_use: formData.substanceUse,
        family_history: formData.familyHistory,
        family_origin: formData.familyOrigin,
        family_psycho_ill: formData.familyPsychIllness ? 1 : 0,
        general_appearance: formData.generalAppearance,
        cranial_nerve: formData.cranialNerve,
        sensory_system: formData.sensorySystem,
        motor_system: formData.motorSystem,
        meningeal_irritation: formData.meningealIrritation,
        cerebellar_signs: formData.cerebellarSigns,
        pallor: formData.pallor,
        jaundice: formData.jaundice,
        cyanosis: formData.cyanosis,
        cut_marks: formData.cutMarks,
        clubing: formData.clubing,
        thyroid: formData.thyroid,
        lymph_nodes: formData.lymphNodes,
        burn_marks: formData.burnMarks,
        dehydration: formData.dehydration,
        edema: formData.edema,
        abnormal_movement_or_tremors: formData.abnormalMovementOrTremors,
        bulk_right_upper: formData.bulkRightUpper,
        bulk_right_lower: formData.bulkRightLower,
        bulk_left_upper: formData.bulkLeftUpper,
        bulk_left_lower: formData.bulkLeftLower,
        tone_right_upper: formData.toneRightUpper,
        tone_right_lower: formData.toneRightLower,
        tone_left_upper: formData.toneLeftUpper,
        tone_left_lower: formData.toneLeftLower,
        power_right_upper: formData.powerRightUpper,
        power_right_lower: formData.powerRightLower,
        power_left_upper: formData.powerLeftUpper,
        power_left_lower: formData.powerLeftLower,
        reflexes_right_upper: formData.reflexesRightUpper,
        reflexes_right_lower: formData.reflexesRightLower,
        reflexes_left_upper: formData.reflexesLeftUpper,
        reflexes_left_lower: formData.reflexesLeftLower,
    });

    const buildPartTwo = () => ({
        respiratory_inspection: formData.respiratoryInspection,
        respiratory_palpation: formData.respiratoryPalpation,
        respiratory_percussion: formData.respiratoryPercussion,
        respiratory_auscultation: formData.respiratoryAuscultation,
        abdominal_inspection: formData.abdominalInspection,
        abdominal_palpation: formData.abdominalPalpation,
        abdominal_percussion: formData.abdominalPercussion,
        abdominal_auscultation: formData.abdominalAuscultation,
        cardio_inspection: formData.cardioInspection,
        cardio_palpation: formData.cardioPalpation,
        cardio_percussion: formData.cardioPercussion,
        cardio_auscultation: formData.cardioAuscultation,
        akathisia: formData.akathisia,
        dystonia: formData.dystonia,
        td: formData.td,
        parkinsons: formData.parkinsons,

        provisional_diagnosis: formData.provisionalDiagnosis,
        differential_diagnosis: formData.differentialDiagnosis,
        diagnosis_treatment_details: formData.diagnosisTreatmentDetails,
        consent_taken_client: formData.consentTakenClient ? 1 : 0,
        consent_taken_family: formData.consentTakenFamily ? 1 : 0,
        consent_taken_guardian: formData.consentTakenGuardian ? 1 : 0,
        consent_taken_term: formData.consentTakenTerm,

        approx_length_of_stay: formData.approxLengthOfStay,
        diagnostic_workup: formData.diagnosticWorkup,
        pharmacotherapy: formData.pharmacotherapy,
        treatment_others: formData.treatmentOthers,

        consultants: formData.consultants,
        resident_name: formData.residentName,
        resident_cell_number: formData.cellNumber,
        resident_sign_date: formData.residentSignDate,
        resident_time: formData.residentTime,
        consultant_doctors_name: formData.doctorsName,
        consultant_sign_date: formData.consultantSignDate,
        consultant_time: formData.consultantTime,
        unit: formData.unit,

        date_of_admission: formData.dateOfAdmission,
        time_of_admission: formData.timeOfAdmission,
        type_of_admission: formData.typeOfAdmission,
        mode_of_transportation: formData.modeOfTransportation,
        accompanied_by: formData.accompaniedBy,
        id_band: formData.idBand,

        nursing_age: formData.nursingAge,
        weight: parseFloat(formData.nursingWeight) || 0,
        height: parseFloat(formData.nursingHeight) || 0,
        blood_pressure: formData.bloodPressure,
        temperature: parseFloat(formData.temperature) || 0,
        pulse: parseInt(formData.pulse) || 0,
        resp_rate: parseInt(formData.respRate) || 0,
        o2_saturation: parseFloat(formData.o2Saturation) || 0,

        reason_for_admission: formData.reasonForAdmission,

        communicable_disease: Array.isArray(formData.communicableDisease) ? formData.communicableDisease.join(', ') : formData.communicableDisease,
        communicable_none: formData.communicable_None,
        communicable_other: formData.communicable_Others,
        isolation_type: formData.isolationType,
        isolation_other: formData.isolationOther,
        eye_glasses: formData.eyeGlasses,
        hearing_aids: formData.hearingAids,
        dentures: formData.dentures,
        jewelry: formData.jewelry,
        others: formData.personalOthers,
    });

    const buildPartThree = () => ({
        mdr_organisms: Array.isArray(formData.mdrOrganisms) ? formData.mdrOrganisms.join(', ') : formData.mdrOrganisms,
        informed_valuable_policy: formData.informedValuablePolicy,
        presence_of_pain: formData.presenceOfPain,
        pain_score: formData.painScore,
        pain_scale_used: Array.isArray(formData.painScaleUsed) ? formData.painScaleUsed.join(', ') : formData.painScaleUsed,
        pain_location: formData.painLocation,
    });

    const buildPartFour = () => ({
        level_of_conscious: formData.levelOfConscious,
        sensory: formData.sensory,
        sensory_others: formData.sensoryOthers,
        vision: formData.vision,
        vision_others: formData.visionOthers,
        hearing: formData.hearing,
        hearing_others: formData.hearingOthers,
        speech: formData.speech,
        speech_others: formData.speechOthers,
        self_caring: Array.isArray(formData.selfCaring) ? formData.selfCaring.join(', ') : formData.selfCaring,
        self_caring_other: formData.selfCaringOther,
        musculoskeletal: Array.isArray(formData.musculoskeletal) ? formData.musculoskeletal.join(', ') : formData.musculoskeletal,
        musculoskeletal_other: formData.musculoskeletalOther,
        assisting: Array.isArray(formData.assisting) ? formData.assisting.join(', ') : formData.assisting,
        assisting_other: formData.assistingOther,
        admission_items: Array.isArray(formData.admissionItems) ? formData.admissionItems.join(', ') : formData.admissionItems,
        cognition: Array.isArray(formData.cognition,) ? formData.cognition.join(', ') : formData.cognition,
        client_level: formData.clientLevel,
        client_level_others: formData.clientLevelOthers,
        room_allocation: formData.roomAllocation,
        inform_medicine: formData.informMedicine,
        guide_family_meetings: formData.guideFamilyMeetings,
        info_consultants: formData.infoConsultants,
        authorize_hair_cutting: formData.authorizeHairCutting,
        isolation_contagious: formData.isolationContagious,
        weight_kg: parseFloat(formData.weightKg) || 0,
        height_cm: parseFloat(formData.heightCm) || 0,
        bmi: parseFloat(formData.bmi) || 0,
        bmi_less_18: formData.bmiLess18,
        bmi_greater_25: formData.bmiGreater25,
        lost_weight_3_months: formData.lostWeight3Months,
        recent_hospitalization: formData.recentHospitalization,
        dietitian_referral_conditions: formData.dietitianReferralConditions,
        refer_dietitian: formData.referDietitian,
        roh_nursing: formData.rohNursing,
        ros_nursing: formData.rosNursing,
        rov_nursing: formData.rovNursing,
        roe_nursing: formData.roeNursing,
        rof_nursing: formData.rofNursing,
        rovu_nursing: formData.rovuNursing,
        suicide_form_nursing: formData.suicideFormNursing2,
        violence_form_nursing: formData.violenceFormNursing2,
        safety_care_plan_nursing: formData.safetyCarePlanNursing2,
        appearance: formData.appearance,
        behavior: formData.behavior,
        speech_mse: formData.speechMse,
        mood_affect: formData.moodAffect,
        thought_process: formData.thoughtProcess,
        thought_content: formData.thoughtContent,
        insight: formData.insight,
        memory: formData.memory,
        self_esteem: formData.selfEsteem,
        hopelessness: formData.hopelessness,
        worthlessness: formData.worthlessness,
        coping: formData.coping,
        marital_status_socio: formData.maritalStatusSocio,
        no_of_children: parseInt(formData.noOfChildren) || null,
        work_situation: formData.workSituation,
        occupation: formData.occupationSocio,
        habit: formData.habit,
        habit_specify: formData.habitSpecify,
        living_situation: formData.livingSituation,
        living_situation_specify: formData.livingSituationSpecify,
    });

    const buildPartFive = () => ({
        religion: formData.religion,
        religion_other: formData.religionOther,
        religious_practices: formData.religiousPractices,
        privacy: formData.privacy,
        food: formData.food,
        gender_regalia: formData.genderRegalia,
        other_values: formData.otherValues,
        mouth_tongue: formData.mouthTongue,
        oral_sign_symptom: formData.oralSignSymptom,
        oral_sign_symptom_others: formData.oralSignSymptomOthers,
        ng_gastric: formData.ngGastric,
        ng_gastric_date: formData.ngGastricDate,
        skin_appearance: formData.skinAppearance,
        skin_appearance_other: formData.skinAppearanceOther,
        skin_temperature: formData.skinTemperature,
        skin_contagious: formData.skinContagious,
        skin_location: formData.skinLocation,
        skin_reasons: formData.skinReasons,
        cardio_pulse: formData.cardioPulse,
        cardio_sign_symptom: formData.cardioSignSymptom,
        cardio_sign_symptom_other: formData.cardioSignSymptomOther,
        respiration: formData.respiration,
        respiration_other: formData.respirationOther,
        cough: formData.cough,
        bowel_movement: formData.bowelMovement,
        bowel_sign_symptom: formData.bowelSignSymptom,
        urinary_appearance: formData.urinaryAppearance,
        urinary_sign_symptom: formData.urinarySignSymptom,
        date_of_insertion: formData.dateOfInsertion,
        catheter_type: formData.catheterType,
        urinary_others: formData.urinaryOthers,
        obs_gyn: formData.obsGyn,
        menstrual_cycle: formData.menstrualCycle,
        dysmenorrhea: formData.dysmenorrhea,
        skin_changes: formData.skinChanges,
        obs_gyn_others: formData.obsGynOthers,
        nursing_staff: formData.nursingStaff,
        checked_by: formData.checkedBy,
        designation: formData.designation,
        nursing_date: formData.nursingDate,
        nursing_time: formData.nursingTime,
        signature: formData.signature,
        head_nurse: formData.headNurse,
        clinical_nurse_instructor: formData.clinicalNurseInstructor,
        team_leader: formData.teamLeader,
        irp_name: formData.irpName,
        irp_date: formData.irpDate,
        irp_domain: formData.domain,
        irp_rehab_goal: formData.rehabGoal,
        irp_strengths: formData.strengths,
        irp_skills_needed: formData.skillsNeeded,
        irp_client_signature: formData.clientSignature,
        irp_client_date: formData.clientDate,
        irp_practitioner_signature: formData.practitionerSignature,
        irp_practitioner_date: formData.practitionerDate,
        endorsement: formData.endorsement,
    });

    const buildPartSix = () => ({
        discharge: formData.discharge,
        discharge_instruction_to: formData.dischargeInstructionTo,
        special_instructions: formData.specialInstructions,
        injections: formData.injections,
        any_red_flag: formData.anyRedFlag,
        risk_assessment_dc: formData.riskAssessmentDC,
        expected_discharge_date: formData.expectedDischargeDate,
        home_care: formData.homeCare,
        home_care_others_specify: formData.homeCareOthersSpecify,
        transportation: formData.transportation,
        psychotherapy: formData.psychotherapy,
        day_rehab: formData.dayRehab,
        day_rehab_days: formData.dayRehabDays,
        long_term_care: formData.longTermCare,
        other_needs: formData.otherNeeds,
        other_needs_specify: formData.otherNeedsSpecify,
        educate_illness: formData.educateIllness ?? "",
        educate_hygiene: formData.educateHygiene ?? "",
        educate_medication: formData.educateMedication ?? "",
        unusual_behavior: formData.unusualBehavior ?? "",
        giving_medicine: formData.givingMedicine ?? "",
        contact_doctor: formData.contactDoctor ?? "",
        client_sharing: formData.clientSharing ?? "",
        engaged_activities: formData.engagedActivities ?? "",
        trigger_relapse: formData.triggerRelapse ?? "",
        sleep_wake: formData.sleepWake ?? "",
        eating_meals: formData.eatingMeals ?? "",
        physical_activity: formData.physicalActivity ?? "",
        coping_strategies: formData.copingStrategies ?? "",
        preventing_relapse: formData.preventingRelapse ?? "",
        socially_connected: formData.sociallyConnected ?? "",
        diet: formData.diet ?? "",
        take_home_medication: formData.takeHomeMedication ?? "",
        compliance_treatment: formData.complianceTreatment ?? "",
        followup_visit: formData.followupVisit ?? "",
        effective_coping: formData.effectiveCoping ?? "",
        hospital_emergency: formData.hospitalEmergency ?? "",
        activity_home: formData.activityHome ?? "",
        stress_management: formData.stressManagement ?? "",
        aggression_management: formData.aggressionManagement ?? "",
        alarming_signs: formData.alarmingSigns ?? "",
        occupational_therapy: formData.occupationalTherapy ?? "",
        social_support: formData.socialSupport ?? "",

        environmental_safety: formData.environmentalSafety ?? "",
        process_giver_role_strain: formData.processGiverRoleStrain ?? "",
        long_term_facilities: formData.longTermFacilities ?? "",
        contact_number: formData.contactNumber ?? "",
        others: formData.others ?? "",
        name_signature: formData.nameSignature ?? "",
        signature_date: formData.signatureDate ?? "",
        signature_time: formData.signatureTime ?? "",

        possible_relapse_sign: formData.possibleRelapseSign ?? "",
        recovery_goals: formData.recoveryGoals ?? "",
        who_to_contact: formData.whoToContact ?? "",
        where_to_go_crises: formData.whereToGoCrises ?? "",
        details_medication: formData.detailsMedication ?? "",
        detail_treatment_plan: formData.detailTreatmentPlan ?? "",
        physical_health_needs: formData.physicalHealthNeeds ?? "",
        date_of_review: formData.dateOfReview ?? "",
        admission_pathway: formData.admissionPathway ?? "",
        risk_assessment_safety_plan: formData.riskAssessmentSafetyPlan ?? "",
        doctor_name: formData.doctorName ?? "",
        guardian_signature: formData.guardianSignature ?? "",
        guardian_name: formData.guardianName ?? "",
        name_of_documents: formData.nameOfDocuments ?? "",
        effective_date: formData.effectiveDate ?? "",
        approved_by: formData.approvedBy ?? "",
        approved_date: formData.approvedDate ?? "",
        next_revision_by: formData.nextRevisionBy ?? "",
        next_revision_date: formData.nextRevisionDate ?? "",
    });

    const buildMonthlyNotes = () => ({
        note_date: formData.date,
        date: formData.date,
        progress_note: formData.progressNote,
        family_meeting_minutes: formData.familyMeetingMinutes,
        discussion_summary: formData.discussionSummary,
    });

    const buildFullPayload = () => ({
        patient_id: formData.patientId,
        case_id: formData.caseId,
        mr_number: formData.mrNumber,
        date: formData.date,
        time: formData.time,
        name: formData.name,
        age: parseInt(formData.age) || 0,
        occupation: formData.occupation,
        gender: formData.gender,
        marital_status: formData.maritalStatus,
        source_of_referral: formData.sourceOfReferral,
        informant: formData.informant,
        comorbid: formData.comorbid,
        consent_type: formData.consentType,
        consent_reason: formData.consentReason,
        presenting_complaints: formData.presentingComplaints,
        psycho_social_stressors: formData.psychoSocialStressors,
        functional_adjustment: formData.functionalAdjustment,
        vegetative_functions: formData.vegetativeFunctions,
        current_treatment: formData.currentTreatment,
        allergies: formData.allergies,
        duration: formData.duration,
        episodes: formData.episodes,
        ect: formData.ect,
        course: formData.course,
        compliance: formData.compliance,
        admission: formData.admission,
        opd_fu: formData.opdFU,
        lai: formData.lai,
        eps_side_effects: formData.epsSideEffects,
        roh: formData.roh,
        ros: formData.ros,
        rov: formData.rov,
        roe: formData.roe,
        rof: formData.rof,
        rovu: formData.rovu,
        risk_of_suicide: formData.suicideFormNursing,
        risk_of_violence: formData.violenceFormNursing,
        time_of_discharge: formData.safetyCarePlanNursing,

        part_one: buildPartOne(),
        part_two: buildPartTwo(),
        part_three: buildPartThree(),
        part_four: buildPartFour(),
        part_five: buildPartFive(),
        part_six: buildPartSix(),
        monthly_pro_notes: (formData.monthlyProgress || []).map(item => ({
            // 1. Map the dates from the specific row (fallback to main date if empty)
            note_date: item.progressDate || formData.date,
            date: item.meetingDate || formData.date,

            // 2. Map 'update' from UI to 'progress_note' for Backend
            progress_note: item.update,

            // 3. Map the other fields from the row item, not the root formData
            family_meeting_minutes: item.familyMeetingMinutes,
            discussion_summary: item.discussionSummary
        })),
        nursing_needs: (formData.nursingProblems || [])
            .filter(item => item.problem)
            .map(item => ({
                nursing_need: item.problem
            })),
        obj_act_plan: (formData.objectives || [])
            .filter(item => item.name)
            .map(item => ({
                objective: item.name,
                action_status: item.obj_status,
                date: item.obj_date,
                steps: (item.actions || []).map(step => ({
                    action_step: step.action_name, // Map form's 'action_name' to API's 'action_step'
                    location: step.location,
                    frequency: step.frequency,
                    duration: step.duration
                }))
            })),
        problem_list: (formData.generalProblems || [])
            .filter(item => item.problem)
            .map(item => ({
                problem: item.problem
            })),
        cognitive_assessment: formData.cognitive_assessment,
        discharge_planning: formData.discharge_planning,
    });

    const handleFullSubmit = async (e) => {
        e.preventDefault();

        if (!validateRequiredFields()) return;

        const payload = buildFullPayload();

        try {
            const res = await dispatch(createIntegratedAssessment(payload)).unwrap();
            Swal.fire("Saved!", "Integrated Assessment created successfully.", "success");
            navigate(`/residential/phase-01/integrated-initial-assessment/edit/${res.id || res.data?.id || (res && res[0]?.id)}`, { state: { patientId: patientId || (res && res.patient_id), caseId: caseId || (res && res.case_id) } });
        } catch (err) {
            Swal.fire("Error!", getApiErrorMessage(err, "Failed to save the Integrated Assessment."), "error");
        }
    };

    return (
        <MainLayout>
            <div className="container-xxl flex-grow-1 container-p-y">
                <Breadcrumbs />
                <div className="card">
                    <div className="card-body">
                        <form onSubmit={handleFullSubmit}>
                            <IntegratedInitialAssessmentForm
                                formData={formData}
                                setFormData={setFormData}
                                handleChange={handleChange}
                                onSectionSave={handleSectionSave}
                                savingSection={savingSection}
                            />
                            <div className="col-12 d-flex justify-content-end mt-3 gap-2">
                                <button type="submit" className="btn btn-primary px-4 sticky-form-submit" disabled={savingSection}>
                                    <i className="ri-save-2-line me-1"></i> Save Full Assessment
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </MainLayout>
    );
};

export default AddIntegratedInitialAssessment;